/* eslint-disable react-hooks/exhaustive-deps */
import React, {
	useState,
	useMemo,
	useEffect,
	useRef,
	useCallback,
	useContext,
} from 'react';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { memoize } from 'lodash';
import Socket, { ConnectedUser } from '../../connection/socket';
import { VideoStream } from '../types/videos';
import * as BroadcastActions from '../../stores/actions/broadcasting';
import {
	getFullAssetURI,
	toMap,
	numberToPercent,
	percMultiply,
	hexToRgb,
	convertPxToRem,
	getVideoRect,
	calculateBorderSize,
	isVideo,
} from '../../utils/utils';
import VideoButtons from './video-buttons';
import Title from './title';
import FeedMedia from './feed-media';
import FeedPresenter from './feed-presenter';
import FeedPlaceholder from './feed-placeholder';
import FeedEmail from './feed-email';
import FeedScreenShare from './feed-screenshare';
import { GreenroomState } from '../../stores/types/types';
import V_LAYOUTS, { fixedLayoutTypes } from '../../utils/video-layout';
import { ShotFeed, FEED_TYPES } from '../../stores/reducers/shots';
import useShots from '../../contexts/shots/provider';
import CustomGraphicsCanvas from '../custom-graphics/custom-graphics-canvas';
import { videoGridClasses } from '../../types/constants';
import OverlayDropArea from '../custom-graphics/overlay-drop-area';
import { DebugGate } from '../debug-views/debug-panel/debug-gate';
import {
	DragItem, DRAG_TYPE, DropAreaData, DropCallbackData,
} from '../../stores/reducers/user-interface';
import AudioOnlyFeeds from './audio-only-feeds';
import { showAlert } from '../general-components/alert-service';
import { ALERT_TYPES } from '../general-components/alert';
import { PresentedMediaItem } from '../../stores/reducers/media';
import * as ShotsActions from '../../stores/actions/shots';
import { getShotStartTimestampEntity } from '../../connection/shots';
import PreloadVideo from '../../utils/preload-video';
import { VideoCacheContext } from '../../contexts/media/video-queue';
import { SpotlightContext } from '../spotlight/spotlight-context';
import SpotlightConnector from '../spotlight/spotlight-connector';
import MediaVideo from '../general-components/media-video';
import { connectorIdLive, projectedComponentIdEditing, projectedComponentIdLive } from '../spotlight/spotlight-utils';
import { shouldDoEchoCancel } from '../../hooks/use-echo-cancellation';
import MediaVideoSpotlight from '../general-components/media-video-spotlight';
import SpotlightManager from '../spotlight/spotlight-manager';
import FeedMediaSpotlight from './feed-media-spotlight';

const lowTone = require('../../audio/low-tone.mp3');

const doEchoCancel = shouldDoEchoCancel();

/*

WARNING: Our broadcasting application "bullman" loads the Greenroom with "embed" view on.
	Our broadcasting server doesn't have a logged in user. It essentially loads the main video player
	in fullscreen mode which is what is broadcasted. So don’t assume the ‘user’ variable
	from sessionReducer is going to be defined in any of the components that the embed
	view uses or else that would cause a bug in the broadcast.

Also, when you make changes to main-video-live and child components test
that the embed view is working as you would expect by passing the meeting_id into
https://greenroom.brandlive-dev.com/embed/[meeting_id]?debug=embed.
This is what the broadcasting server loads.

*/

interface OwnProps {
	isHost: boolean;
	iterateLoadedMediaCount: Function;
	socket: Socket | null;
	useAgora: boolean;
	useTwilio: boolean;
}

export default function MainVideoLive(props: OwnProps) {
	const { socket, iterateLoadedMediaCount } = props;
	const connectedPresenterVideos = useSelector(
		(state: GreenroomState) => state.VideoReducer.connectedPresenterVideos,
	);
	const clearShot = useSelector((state: GreenroomState) => state.SettingsReducer.clearShot);
	const host = useSelector((state: GreenroomState) => state.SessionReducer.host);
	const user = useSelector((state: GreenroomState) => state.SessionReducer.user);
	const meeting = useSelector((state: GreenroomState) => state.SessionReducer.meeting);
	const connectedUsers = useSelector(
		(state: GreenroomState) => state.SessionReducer.connectedUsers,
	);
	const embed = useSelector((state: GreenroomState) => state.SessionReducer.embed);
	const token = useSelector((state: GreenroomState) => state.SessionReducer.token);
	const { use_spotlight: useSpotlight } = useSelector((state: GreenroomState) => state.SettingsReducer.featureFlags);
	const streamState = useSelector((state: GreenroomState) => state.BroadcastReducer.streamState);
	const debugOverlaysEnabled = useSelector(
		(state: GreenroomState) => state.DebugReducer.debugOverlaysEnabled,
	);
	const globalIsDragging = useSelector(
		(state: GreenroomState) => state.UserInterfaceReducer.globalIsDragging,
	);
	const typeOfDragging = useSelector(
		(state: GreenroomState) => state.UserInterfaceReducer.typeOfDragging,
	);
	const media = useSelector((state: GreenroomState) => state.MediaReducer.media);
	const presentedMedia = useSelector(
		(state: GreenroomState) => state.MediaReducer.presentedMedia,
	);
	const canvasStyle = useSelector((state: GreenroomState) => state.ShotsReducer.canvasStyle);
	const { preloadedElements } = useContext(VideoCacheContext);

	const {
		backgroundColor,
		backgroundType, // image, video, or color. Video not yet implemented
		wallpaperColor, // color in hex
		wallpaperType,
		wallpaperScale,
		wallpaperImage,
		backgroundImage,
		backgroundScale, // a percentage to fill
		presenterVideoBorderOn, // borders on individual videos
		presenterVideoBorderColor, // hex color
		presenterVideoBorderOpacity, // 0.0 to 1.1
		// center, inside, or outside (i'm not sure this is possible tbh since
		// it would just be a css border prop)
		presenterVideoBorderPosition,
		presenterVideoBorderSize, // width of video border
		presenterVideoRadius,
		layoutSpacingPresets,
	} = canvasStyle;

	const {
		currentShot,
		nextShot,
		feedDroppedOnCanvas,
		overlayDroppedOnCanvas,
	} = useShots();

	const {
		shot_data: {
			shotFeeds,
			forcedLayout,
			shotGraphics,
			videoTitlesOn,
		},
	} = currentShot;

	const dispatch = useDispatch();
	const videoWrapperRef = useRef<HTMLDivElement | null>(null);
	const streamStateRef = useRef<number>(streamState);
	const [awaitingProducer, setAwaitingProducer] = useState(false);
	const [videoWidthHeight, setVideoWidthHeight] = useState({
		height: '0', width: '0', top: '0px', left: '0px', bottom: '0px', right: '0px',
	});
	const [selectedMediaFeed, setSelectedMediaFeed] = useState<number | undefined>();
	const standardFeeds = useMemo(() => (shotFeeds?.length > 0
		? shotFeeds.filter((shotFeed) => !shotFeed.audioOnly)
		: []), [shotFeeds]);
	const audioOnlyFeeds = useMemo(() => (shotFeeds?.length > 0
		? shotFeeds.filter((shotFeed) => shotFeed.audioOnly)
		: []), [shotFeeds]);
	const [showAudioOnlyFeedsButton, setShowAudioOnlyFeedsButton] = useState(false);

	useEffect(() => { streamStateRef.current = streamState; }, [streamState]);
	useEffect(() => {
		if (embed && token) {
			dispatch(BroadcastActions.getDatabaseStreamState(token, meeting!.meeting));
		}
		// this is so the audio only button doesn't show up at top then flash down to bottom
		setShowAudioOnlyFeedsButton(true);
	}, []);

	useEffect(() => {
		// We want to support multiple decks in canvas but need arrow keys to only
		// control one deck
		const firstFoundMediaItem = shotFeeds.find((feed) => feed.media);

		if (firstFoundMediaItem && firstFoundMediaItem.media) {
			// Might eventually want to pass media id and initial position:
			// setSelectedMediaFeed(parseInt(`${firstFoundMediaItem.media}0`, 10));
			setSelectedMediaFeed(firstFoundMediaItem.media);
		}
	}, [shotFeeds]);

	const sendEmbedStateUpdate = (currentStreamState: number) => {
		socket?.sendMessage('embed-state-update', {
			newStreamState: currentStreamState,
		});
	};

	useEffect(() => {
		// Call beehive for shotTimeStartEntity

		const requestShotTimeStartEntity = async () => {
			if (token && meeting) {
				const shotTimestamp = await getShotStartTimestampEntity(token, meeting?.meeting);
				if (!shotTimestamp) return;
				dispatch(ShotsActions.setPlayingShot(shotTimestamp));
			}
		};

		requestShotTimeStartEntity();

		// listener for requesting the pre-show/post-show media timestamp from embed app
		if (host) socket?.sendMessage('request-embed-state', {});

		socket?.sendMessage('request-media-video-state', {});

		return () => {
			socket?.removeListener('request-playing-shot-state');
		};
	}, []);

	useEffect(() => {
		// these need refs to fire
		if (embed) { socket?.addListener('request-embed-state', () => sendEmbedStateUpdate(streamState)); }

		return () => {
			if (embed) { socket?.removeListener('request-embed-state'); }
		};
	}, [streamState, socket]);

	const [r, g, b] = useMemo(() => hexToRgb(presenterVideoBorderColor), [presenterVideoBorderColor]);

	const backgroundStyle: any = useMemo(() => {
		const newStyle: any = {};
		newStyle.backgroundColor = backgroundColor;
		if (backgroundType === 'image') {
			newStyle.backgroundSize = `${backgroundScale}%`;
			newStyle.backgroundImage = `url('${getFullAssetURI(backgroundImage)}')`;
		}
		newStyle.display = 'grid';
		return newStyle;
	}, [backgroundColor, backgroundImage, backgroundScale, backgroundType]);

	const wallpaperStyle: any = useMemo(() => {
		const newStyle: any = {};

		newStyle.backgroundColor = wallpaperColor;
		if (wallpaperType === 'image') {
			newStyle.backgroundSize = `${wallpaperScale}%`;
			newStyle.backgroundImage = `url('${getFullAssetURI(wallpaperImage)}')`;
		}

		return newStyle;
	}, [wallpaperColor, wallpaperScale, wallpaperImage, wallpaperType]);

	function margins() {
		// We created some media specific layouts that have "media" in their type title.
		// These layouts have been calculated for 16:9 and 4:3 media layouts with specific
		// presenter sizes and gaps as well.
		// When these are selected we ignore spacing settings (borders still work though).
		const isFixedLayout = forcedLayout
			? fixedLayoutTypes.some((layout) => forcedLayout.includes(layout))
			: false;

		if (isFixedLayout) {
			return {
				columnGap: 0,
				rowGap: 0,
				paddingTop: 0,
				paddingBottom: 0,
				paddingRight: 0,
				paddingLeft: 0,
			};
		}
		const layoutMap = toMap('layout', layoutSpacingPresets);
		// if no layout exists set no margins
		const layout = layoutMap.get(forcedLayout);
		const backup = {
			spacing: {
				top: 0, bottom: 0, side: 0, inner: 0,
			},
		};
		const marginSpacing = layout?.spacing ? layout : backup;
		const {
			inner, top, side, bottom,
		} = marginSpacing.spacing;
		return {
			columnGap: numberToPercent(inner),
			rowGap: percMultiply(numberToPercent(inner) || '0%'),
			paddingTop: numberToPercent(top),
			paddingBottom: numberToPercent(bottom),
			paddingRight: numberToPercent(side),
			paddingLeft: numberToPercent(side),
		};
	}

	const marginMemo = useMemo(() => margins(), [forcedLayout, shotFeeds, layoutSpacingPresets]);

	useEffect(() => {
		if (!awaitingProducer) return;
		if (connectedUsers.find((u: any) => u.isProducer)) setAwaitingProducer(false);
	}, [connectedUsers]);

	const mainVideoClassesMemo: any[] = useMemo(() => [
		'main-video',
		forcedLayout,
		{
			'rounded-border': !host && !embed,
			'rounded-border-thumbnail': !host && !embed,
			backgroundColor: wallpaperColor,
		},
	], [forcedLayout, host, embed, wallpaperColor]);

	const hasPictureInPictureMemo = useMemo(
		() => forcedLayout.includes('picture-in-picture'),
		[forcedLayout],
	);

	const getScreenShareFromEmail = useCallback((email: string) => {
		const cUser = connectedUsers.find(
			(connectedUser: ConnectedUser) => (
				connectedUser.email === email && connectedUser.screenShareWebrtcId
			),
		);
		return connectedPresenterVideos.find(
			(vid: VideoStream) => vid.id === cUser?.screenShareWebrtcId,
		);
	}, [connectedUsers, connectedPresenterVideos]);

	const numberOfVideosOnCanvas = () => {
		const vidNumber = currentShot.shot_data.shotFeeds.reduce(
			(previousValue:number, currentValue:ShotFeed) => {
				const singleMedia = media.find((elm) => elm.id === currentValue.media);
				let videoCheck = false;
				if (singleMedia) {
					videoCheck = isVideo(singleMedia.type);
				}
				return previousValue + (videoCheck ? 1 : 0);
			},
			0,
		);
		return vidNumber;
	};

	// we resolve drops in this component because it's the common ancestor
	// of the feed components, so we can pass down the function to call on drop
	const feedDrop = (item: DragItem, dropAreaData?: DropAreaData) => {
		const mediaBeingReplacedId = shotFeeds.find(
			(shot) => shot.position === dropAreaData?.feedPosition,
		)?.media;
		const mediaItemBeingReplaced = media.find((i) => i.id === mediaBeingReplacedId);
		const replacingItem = media.find((mediaItem) => mediaItem.id === item.id);
		const beingReplacedIsAVideo = isVideo(mediaItemBeingReplaced?.type || '');
		const replacingItemIsVideo = isVideo(replacingItem?.type || '');
		const videoIsInCanvas = shotFeeds.some(
			(shot) => shot.media === replacingItem?.id,
		);
		const canDropItem = replacingItemIsVideo
			? (numberOfVideosOnCanvas() < 8)
				|| (numberOfVideosOnCanvas() === 8 && beingReplacedIsAVideo) || videoIsInCanvas
			: true;
		if (typeof dropAreaData?.feedPosition === 'number' && canDropItem) {
			feedDroppedOnCanvas(item, socket, false, dropAreaData.feedPosition);
		} else {
			showAlert({
				type: ALERT_TYPES.WARNING,
				message: 'Video Capacity Reached',
				description: 'Only 8 videos are allowed on the canvas at a given time',
				duration: 5000,
			});
		}
	};

	const overlayDrop = (item: DragItem, dropData: DropCallbackData) => {
		overlayDroppedOnCanvas(item, socket, false, dropData);
	};

	// If video, rerenders the media feed component, to reset the video machine references.
	// If image, to avoid long blank render between slides by not changing key.
	const getKeyForMediaFeed = memoize((id: number): string => {
		const presMediaItem = presentedMedia.find((med: PresentedMediaItem) => med.mediaId === id);
		const medItem = media.find((m) => m.id === id);
		if (presMediaItem && medItem) {
			const page = medItem?.media_data.image_array[presMediaItem.currentPage - 1];
			if (page && isVideo(page)) {
				return `${presMediaItem.currentPage}-${id}`;
			}
		}
		return `1-${id}`;
	});

	const videosInCanvas = useCallback((
		selectedShotFeeds: ShotFeed[],
		startAt: number = 0,
		audioOnly: boolean = false,
	) => {
		const feedsInShot = selectedShotFeeds.map((shotFeedRef: ShotFeed, mapIndex: number) => {
			const shotFeed = { ...shotFeedRef };
			const index = startAt + mapIndex;
			// if a user was put into a shot before creating an account and joins
			// the room while the shot is on the canvas, change the feed from email
			// to presenter to put them in the canvas without editing the shot and
			// forcing it to rerender
			if (shotFeed.feedType === FEED_TYPES.EMAIL && shotFeed.email) {
				const cUser = connectedUsers.find(
					(connectedUser: ConnectedUser) => connectedUser.email === shotFeed.email,
				);
				if (cUser && cUser.gr_user) {
					shotFeed.feedType = FEED_TYPES.PRESENTER;
					shotFeed.presenter = cUser.gr_user;
					shotFeed.email = null;
				}
			}

			// dry this up a bit
			const sizing = convertPxToRem(
				calculateBorderSize('.main-video', presenterVideoBorderSize, 0),
			);
			const color = `rgba(${r}, ${g}, ${b}, ${presenterVideoBorderOpacity})`;
			const insideStyle: any = {
				borderRadius: presenterVideoRadius,
				position: 'absolute',
				top: 0,
				left: 0,
				right: 0,
				bottom: 0,
				boxShadow: presenterVideoBorderOn
					? `inset 0 0 0 ${sizing}rem ${color}`
					: 'none',
			};

			return (
						<React.Fragment key={`rf-${mapIndex}`}>
							<div
								key={`div-${mapIndex}`}
								className={classNames('video-container-presenter', videoGridClasses[index])}
								style={{
									borderRadius: presenterVideoRadius,
									boxShadow: presenterVideoBorderOn && presenterVideoBorderPosition === 'outside'
										? `0 0 0 ${sizing}rem ${color}`
										: 'none',
									display: (forcedLayout === V_LAYOUTS.TWO.FULL_TWO && index !== 1) ? 'none' : 'block',
									zIndex: 11,
								}}>
								{presenterVideoBorderPosition === 'inside' && (
									<div style={insideStyle} />
								)}
								{shotFeed.feedType === FEED_TYPES.EMPTY && (
									<FeedPlaceholder
										feedPosition={index}
										dropCallback={feedDrop}
									/>
								)}
								{(shotFeed.feedType === FEED_TYPES.MEDIA || shotFeed.feedType === FEED_TYPES.MEDIA_AUDIO) && shotFeed.media
									 && (useSpotlight && !audioOnly ? <FeedMediaSpotlight
										key={`fms-${mapIndex}`}
										feedPosition={audioOnly ? shotFeed.position : index}
										mediaId={shotFeed.media}
										socket={socket}
										selectedMediaFeed={selectedMediaFeed}
										onSelectMediaFeedChange={setSelectedMediaFeed}
										iterateLoadedMediaCount={iterateLoadedMediaCount}
										isEditingFeed={false}
										dropCallback={feedDrop}
										audioOnly={audioOnly}
										/>

									 	: 										<FeedMedia
										key={getKeyForMediaFeed(shotFeed.media)}
										feedPosition={audioOnly ? shotFeed.position : index}
										mediaId={shotFeed.media}
										socket={socket}
										selectedMediaFeed={selectedMediaFeed}
										onSelectMediaFeedChange={setSelectedMediaFeed}
										iterateLoadedMediaCount={iterateLoadedMediaCount}
										isEditingFeed={false}
										dropCallback={feedDrop}
										audioOnly={audioOnly}
									/>)
								}
								{shotFeed.feedType === FEED_TYPES.PRESENTER && shotFeed.presenter && (
									<FeedPresenter
										feedPosition={index}
										video={connectedPresenterVideos.find(
											(vid: VideoStream) => vid.profile === shotFeed.presenter,
										)}
										grUser={shotFeed.presenter}
										dropCallback={feedDrop}
									/>
								)}
								{shotFeed.feedType === FEED_TYPES.EMAIL && shotFeed.email && (
									<FeedEmail
										feedPosition={index}
										email={shotFeed.email}
										dropCallback={feedDrop}
									/>
								)}
								{shotFeed.feedType === FEED_TYPES.SCREENSHARE && shotFeed.screenshare && (
									<FeedScreenShare
										feedPosition={index}
										video={getScreenShareFromEmail(shotFeed.screenshare)}
										email={shotFeed.screenshare}
										dropCallback={feedDrop}
									/>
								)}
								{/* this was added because when there was no feed like screenshare
								//stopped the inside border did not show */}
								{presenterVideoBorderPosition === 'inside' && (
									<div style={insideStyle} />
								)}
								{shotFeed.presenter && (
									<Title
										key={currentShot.shot_id}
										idOfPresenter={shotFeed.presenter}
										shotId={currentShot.shot_id}
										videoTitlesOn={videoTitlesOn}
									/>
								)}
							</div>
						</React.Fragment>
			);
		});
		if (!useSpotlight || audioOnly) return feedsInShot;
		const numberOfFeeds = feedsInShot.length;

		const emptyFeed = (<React.Fragment>
			<div
					key={'partition'}
					style={{
						height: '100%',
						width: '100%',
						overflow: 'hidden',
						position: 'absolute',
						zIndex: 10,
					}}
					></div>
		</React.Fragment>);
		feedsInShot.push(emptyFeed);

		const invisibleFeeds = (index:number) => (
			<React.Fragment key={`rf-${index}`}>
				<div
								key={`div-${index}`}
								className={classNames('video-container-presenter', videoGridClasses[index])}

								style={{
									borderRadius: presenterVideoRadius,
									position: 'absolute',
									zIndex: index,
									display: (forcedLayout === V_LAYOUTS.TWO.FULL_TWO && index !== 1) ? 'none' : 'block',
								}}>
					<FeedMediaSpotlight
						key={`fms-${index}`}
						feedPosition={index}
						notVisible={true}
						mediaId={nextShot?.shot_data?.shotFeeds[index]?.media || 0}
						socket={socket}
						selectedMediaFeed={selectedMediaFeed}
						onSelectMediaFeedChange={setSelectedMediaFeed}
						iterateLoadedMediaCount={iterateLoadedMediaCount}
						isEditingFeed={false}
						dropCallback={feedDrop}
						audioOnly={false}
					/>
				</div>
			</React.Fragment>
		);

		for (let i = 0; i < (8 - numberOfFeeds); i++) {
			feedsInShot.push(invisibleFeeds(numberOfFeeds + i));
		}
		return feedsInShot;
	}, [selectedMediaFeed, shotFeeds, connectedPresenterVideos, media, getKeyForMediaFeed, useSpotlight]);

	function mainVideoView() {
		// When the producer clicks apply changes, we load the changes in a layer behind what
		// is currently live. Once all the vids/imgs are done loading in the layer behind,
		// we switch layers and then remove the old one from the DOM so that there is no
		// "flicker/glitch" where you see the background wallpaper for a split second (sometimes longer)
		if (hasPictureInPictureMemo) {
			return (
				<>
					<div
						style={marginMemo}
						className={classNames(mainVideoClassesMemo, 'layer1')}
					>
						{videosInCanvas(standardFeeds.slice(1), 1)}
					</div>
					<div className={classNames(mainVideoClassesMemo, 'layer2')}>
						{videosInCanvas(standardFeeds.slice(0, 1))}
					</div>
					{!!audioOnlyFeeds.length && (
						<div className={classNames(mainVideoClassesMemo, 'hidden')}>
							{videosInCanvas(audioOnlyFeeds, 0, true)}
						</div>
					)}
				</>
			);
		}

		return (
			<>
				{!!audioOnlyFeeds.length && (
					<div className={classNames(mainVideoClassesMemo, 'hidden')}>
						{videosInCanvas(audioOnlyFeeds, 0, true)}
					</div>
				)}

				<div style={marginMemo} className={classNames(mainVideoClassesMemo, 'layer1')}>
					{videosInCanvas(standardFeeds)}
				</div>
			</>
		);
	}

	const resizeObserver = new ResizeObserver((entries) => {
		// 177%, 56.25%
		const wrapWidth = entries[0].contentRect.width;
		const wrapHeight = entries[0].contentRect.height;

		const styleObject = {
			...getVideoRect(wrapWidth, wrapHeight),
			right: 'auto',
			bottom: 'auto',
		};

		setVideoWidthHeight(styleObject);
	});

	useEffect(() => {
		if (videoWrapperRef.current) { resizeObserver.observe(videoWrapperRef.current); }

		return () => {
			if (videoWrapperRef.current) resizeObserver.disconnect();
		};
	}, [videoWrapperRef]);

	/*
		Extract here to avoid unwanted unmount,
		but probably needs a better approach
	*/
	const graphicsCanvas = useMemo((): JSX.Element => (
		<CustomGraphicsCanvas
			renderedGraphics={shotGraphics}
			canEdit={false}
			videoWidthHeight={videoWidthHeight}
			isMainCanvas={true}
		/>
	), [shotGraphics, videoWidthHeight]);

	return (
		<>
			{embed && (
				<PreloadVideo />
			)}
			{embed && <audio
				src={lowTone}
				autoPlay={true}
				loop={true}
				onPlaying={(e: React.SyntheticEvent<HTMLAudioElement, Event>) => {
					(e.target as HTMLAudioElement).volume = 0.05;
				}}
			></audio>}
			<div className={classNames('left-panel-content', { embed })}>
				<div id="video-wrapper" ref={videoWrapperRef} className={classNames({ embed, 'include-audio-only-feeds': !embed })}>
					{shotFeeds && shotFeeds.length > 0 && !clearShot
						? (
							<div
								style={{
									...backgroundStyle,
									...videoWidthHeight,
								}}
								className={classNames(
									'main-video-container',
									{ 'is-producer': host },
									{ 'rounded-border': !host && !embed, embed },
								)}>
								{
									videoWidthHeight
									&& <DebugGate debugFeatureFlag={debugOverlaysEnabled}>
										{graphicsCanvas}
									</DebugGate>
								}
								{mainVideoView()}
								<div
									id="controls-container-live"
									data-testid="controls-container-live"
									className={classNames('main-video', forcedLayout)}
									style={{ ...marginMemo, pointerEvents: globalIsDragging ? 'none' : 'auto' }}
								/>
								{typeOfDragging === DRAG_TYPE.OVERLAY
									&& <OverlayDropArea dropCallback={overlayDrop} />
								}
								{!embed && (
									<div className="button-row-below-video">
										{showAudioOnlyFeedsButton && (
											<AudioOnlyFeeds isForShotEditorModal={false} socket={socket} />
										)}
										<div className="snackbar-container" id="portal-unmute-snackbar-1" />
									</div>
								)}
							</div>
						) : (
							<div style={{ ...wallpaperStyle, ...videoWidthHeight }} className={classNames(
								`main-video-container ${host || embed ? '' : 'rounded-border'}`,
							)}>
								{!embed && awaitingProducer
									&& <div className="without-producer-container rounded-border">
										<div className="without-producer">
											<span>
												Waiting for Producer
											</span>
										</div>
									</div>
								}
							</div>
						)
					}
				</div>

				{!embed && (
					<div
						className="left-panel-below-video">
						<VideoButtons
							socket={socket}
							shotBeingEdited={currentShot}
							isForShotEditorModal={false}
							// treated as 0th indexed feed
							feedPosition={0}
							dropCallback={feedDrop}
						/>
					</div>
				)
				}
			</div >
		</>
	);
}
